import {
  IPartProTradeBidAward,
  IPartProTradeBidLot,
  IPartProTradeBidVehicleDetail,
  PartProTradeAwardCancelReasonCode,
  PartProTradeCancelReason,
} from "../models/partPro/IPartProTradeBids";

// Cancelling an accepted PartPro Trade bid, and the locked matrix cell it frees. The server is the
// authority on both - these keep the screen in step with it.

export const PARTPRO_TRADE_CANCEL_NOTE_MAX_LENGTH = 500;

// Source strings, so they double as i18next keys
export const partProTradeCancelReasonLabels: Record<PartProTradeCancelReason, string> = {
  [PartProTradeCancelReason.Damaged]: "Damaged",
  [PartProTradeCancelReason.Missing]: "Missing",
  [PartProTradeCancelReason.Inaccessible]: "Inaccessible",
  [PartProTradeCancelReason.Other]: "Other",
};

const codeLabels: Record<PartProTradeAwardCancelReasonCode, string> = {
  DAMAGED: "Damaged",
  MISSING: "Missing",
  INACCESSIBLE: "Inaccessible",
  OTHER: "Other",
  VEHICLE_REJECTED: "Vehicle rejected",
};

/** "Damaged - cracked in two places", or just the reason without a note. */
export function describeAwardCancellation(
  award: Pick<IPartProTradeBidAward, "cancelReason" | "cancelNote"> | null | undefined
): string {
  const label = award?.cancelReason ? codeLabels[award.cancelReason] ?? "Other" : "Cancelled";
  const note = award?.cancelNote?.trim();
  return note ? `${label} - ${note}` : label;
}

/** Trimmed, blank as undefined - a whitespace-only box is no note at all. */
export function normaliseCancelNote(note: string | null | undefined): string | undefined {
  const trimmed = (note ?? "").trim();
  return trimmed === "" ? undefined : trimmed;
}

export function isValidCancelReason(reason: PartProTradeCancelReason | null | undefined): reason is PartProTradeCancelReason {
  return reason !== null && reason !== undefined && reason in PartProTradeCancelReason;
}

export type TradeCellState =
  /** A price typed in - edit freely */
  | { kind: "none" }
  /** An accepted bid - locked until cancelled. lot is null while the bids haven't loaded */
  | { kind: "locked"; lot: IPartProTradeBidLot | null }
  /** A part in the category was flagged and the cancellation is on its way to PartPro */
  | { kind: "cancelling" }
  /** Already cancelled on PartPro - the matrix just hasn't caught up yet */
  | { kind: "cancelled"; lot: IPartProTradeBidLot };

/**
 * Whether a matrix cell is held by an accepted bid. The matrix is cached for a few minutes while
 * the bids are read live, so a cancellation PartPro already has wins over a stale cell.
 */
export function tradeCellState(
  cell: { fromPartProTrade: boolean; partProTradeBidId: string | null; partProTradeCancelPending: boolean } | null | undefined,
  partCategoryId: string,
  detail: IPartProTradeBidVehicleDetail | null | undefined
): TradeCellState {
  if (!cell?.fromPartProTrade) return { kind: "none" };

  const lot =
    (detail?.lots ?? []).find(
      (l) => l.partCategoryId === partCategoryId && (!cell.partProTradeBidId || l.award?.bidId === cell.partProTradeBidId)
    ) ?? null;

  if (lot?.status === "CANCELLED") return { kind: "cancelled", lot };
  if (cell.partProTradeCancelPending) return { kind: "cancelling" };
  return { kind: "locked", lot: lot?.status === "AWARDED" ? lot : null };
}

export function isLockedTradeCell(state: TradeCellState): boolean {
  return state.kind === "locked" || state.kind === "cancelling";
}
